<script lang="ts" setup>
import { type AlertsFeaturesAlertPolisGetPolisAlertCountByAdviseurDto } from '@services/alerts-public/orval-api/model/alertsFeaturesAlertPolisGetPolisAlertCountByAdviseurDto';
import {
  polisAlertsGetCountByAdviseur,
  usePolisAlertsGetCountByAdviseur,
} from '@services/alerts-public/orval-api/polis-alerts/polis-alerts';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { useFocus, useLocalStorage } from '@vueuse/core';
import Fuse from 'fuse.js';
import { computed, onMounted, ref, watch } from 'vue';
import CheckboxField from '@shared/form/CheckboxField/CheckboxField.vue';
import VSearch from '@shared/component/VSearch/VSearch.vue';
import VDataTable from '@shared/component/VDataTable/VDataTable.vue';
import VColumn from '@shared/component/VDataTable/VColumn.vue';
import { useRowLinkNavigation } from '@shared/component/VDataTable/useRowLinkNavigation';

const props = defineProps<{ adviseurnummer?: number }>();

const emit = defineEmits<{
  titleChanged: [value: string];
}>();
const zoekterm = ref<string>('');
const adviseurnummerRef = ref<number>();
const searchBox = ref();
const onlyShowWithAlerts = useLocalStorage('CRM_ONLY_SHOW_RELATIONS_WITH_ALERTS', true);

watch(
  () => props.adviseurnummer,
  (e) => (adviseurnummerRef.value = e),
  { immediate: true },
);

const setZoekterm = (text: string) => (zoekterm.value = text.toLowerCase());

useFocus(searchBox);

const { data, isPending, isError } = usePolisAlertsGetCountByAdviseur(
  {},
  {
    query: {
      select: (result) => result.result,
      queryKey: ['PolisAlertsGetByAdviseur', adviseurnummerRef],
      queryFn: () => polisAlertsGetCountByAdviseur({ adviseurnummer: adviseurnummerRef.value }),
    },
  },
);

const filteredData = computed(() => {
  const filtered =
    data.value?.filter((r) => (onlyShowWithAlerts.value === true ? r.aantal > 0 : true)) ?? [];
  if (!zoekterm.value) return filtered;
  const fuse = new Fuse(filtered, { keys: ['relatienaam', 'relatienummer'], threshold: 0.4 });
  const res = fuse.search(zoekterm.value);
  return res.map((r) => r.item);
});

onMounted(() => emit('titleChanged', 'Alerts beheren'));

const { onRowClicked } =
  useRowLinkNavigation<AlertsFeaturesAlertPolisGetPolisAlertCountByAdviseurDto>({
    rowLink: (adviseur) => ({
      name: 'polisAlertsBeheer',
      params: { relatienummer: adviseur.relatienummer },
      query: { adviseurnummer: adviseurnummerRef.value },
    }),
  });
</script>

<template>
  <div class="flex flex-col gap-8">
    <div>
      <VSearch ref="searchBox" @input="setZoekterm" />
      <CheckboxField
        path="onlyShowWithAlerts"
        sub-label="Alleen relaties tonen met alerts"
        v-model="onlyShowWithAlerts"
      />
    </div>
    <div v-if="isPending">
      <VSkeleton height="lg" :count="4" />
    </div>

    <VInfoBox v-else-if="isError" type="danger" class="mt-8">
      Er is een fout opgetreden bij het laden van de polis alerts.
    </VInfoBox>

    <VDataTable
      v-else-if="!isPending"
      :value="filteredData"
      @row-click="onRowClicked"
      show-row-arrow
      sort-field="relatienummer"
      :sort-order="1"
      borderless
    >
      <VColumn sortable header="Relatienummer" field="relatienummer" />
      <VColumn sortable header="Relatienaam" field="relatienaam" />
      <VColumn sortable header="Aantal alerts" field="aantal" />
    </VDataTable>
  </div>
</template>
